import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ReactComponent as MainLogo } from "../../media/icons/mainLogo.svg";
import { ShellIcon } from "./icons";
import { NavList } from "./navList";
import { useDismiss } from "./hooks";
import styles from "./appShell.module.css";

const FOCUSABLE = "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])";

/**
 * The 256px sidebar: the logo with Hide sidebar, then the accordion nav.
 *
 * @param {Object} props the NavList props plus `onHide` (collapses to the rail)
 */
export const Sidebar = ({ nav, activeId, openMap, onToggle, onHide, inboxCount }) => {
  const { t } = useTranslation();

  return (
    <aside id="app-shell-sidebar" className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        <Link to="/" className={styles.logoLink} aria-label={t("dashboard_shell_logo_label")}>
          <MainLogo className={styles.logoImage} />
        </Link>
        <button
          type="button"
          className={styles.hideButton}
          onClick={onHide}
          aria-label={t("dashboard_shell_hide_sidebar")}
          title={t("dashboard_shell_hide_sidebar")}
        >
          <ShellIcon name="collapse" />
        </button>
      </div>
      <NavList nav={nav} activeId={activeId} openMap={openMap} onToggle={onToggle} inboxCount={inboxCount} accordion />
    </aside>
  );
};

/** One rail button that opens a 240px flyout holding a group's links. */
const RailGroup = ({ group, activeId, open, onToggle, onClose, inboxCount }) => {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const holdsActive = group.items.some(item => item.id === activeId);

  useDismiss(open, onClose, wrapRef, buttonRef);

  return (
    <div ref={wrapRef} className={styles.railGroup}>
      <button
        ref={buttonRef}
        type="button"
        className={cn(styles.railButton, holdsActive && styles.itemActive)}
        aria-label={t(group.label)}
        title={t(group.label)}
        aria-expanded={open}
        onClick={onToggle}
      >
        <ShellIcon name={group.icon} />
      </button>
      {open && (
        <div role="group" aria-label={t(group.label)} className={styles.flyout}>
          <span className={styles.flyoutTitle}>{t(group.label)}</span>
          {group.items.map(item => (
            <Link
              key={item.id}
              to={item.to}
              onClick={onClose}
              className={cn(styles.flyoutItem, item.id === activeId && styles.itemActive)}
              aria-current={item.id === activeId ? "page" : undefined}
            >
              <span className={styles.itemLabel}>{t(item.label)}</span>
              {item.count && inboxCount > 0 && <span className={styles.badge}>{inboxCount}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The 64px rail the sidebar hides to: Feed, Inbox and Profile as icon links, a divider, then one flyout button per group.
 *
 * @param {Object} props `nav`, `activeId`, `inboxCount` as for NavList, and `onShow` (restores the sidebar)
 */
export const Rail = ({ nav, activeId, onShow, inboxCount }) => {
  const { t } = useTranslation();
  const [flyout, setFlyout] = useState(null);
  const close = useCallback(() => setFlyout(null), []);

  return (
    <nav className={styles.rail} aria-label={t("dashboard_shell_nav_label")}>
      <button
        type="button"
        className={cn(styles.railButton, styles.railShow)}
        onClick={onShow}
        aria-label={t("dashboard_shell_show_sidebar")}
        title={t("dashboard_shell_show_sidebar")}
      >
        <ShellIcon name="expand" />
      </button>
      {nav[0].items.map(item => (
        <Link
          key={item.id}
          to={item.to}
          className={cn(styles.railButton, styles.railLink, item.id === activeId && styles.itemActive)}
          aria-label={t(item.label)}
          title={t(item.label)}
          aria-current={item.id === activeId ? "page" : undefined}
        >
          <ShellIcon name={item.icon} />
          {item.count && inboxCount > 0 && <span className={styles.railBadge}>{inboxCount}</span>}
        </Link>
      ))}
      <span className={styles.railDivider} />
      {nav.slice(1).map(group => (
        <RailGroup
          key={group.key}
          group={group}
          activeId={activeId}
          inboxCount={inboxCount}
          open={flyout === group.key}
          onToggle={() => setFlyout(flyout === group.key ? null : group.key)}
          onClose={close}
        />
      ))}
    </nav>
  );
};

/**
 * The 280px drawer under 1320px: a modal dialog with every group open, closed by Escape, the overlay or its close button.
 *
 * @param {Object} props `nav`, `activeId`, `inboxCount` as for NavList, `open` and `onClose`
 */
export const Drawer = ({ nav, activeId, open, onClose, inboxCount }) => {
  const { t } = useTranslation();
  const drawerRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    // Safari and Firefox on a Mac do not focus a button when it is clicked, so the opener can be the body: the menu button then.
    const active = document.activeElement;
    const opener = active && active !== document.body ? active : document.querySelector("[aria-controls='app-shell-drawer']");
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    if (closeRef.current) {
      closeRef.current.focus();
    }

    const onKeyDown = event => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) {
        return;
      }

      const stops = [...drawerRef.current.querySelectorAll(FOCUSABLE)];
      const first = stops[0];
      const last = stops[stops.length - 1];

      if (!drawerRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;

      if (opener && opener.isConnected && typeof opener.focus === "function") {
        opener.focus();
      }
    };
  }, [open, onClose]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a pointer-only overlay; Escape and the close button are the keyboard paths */}
      <div
        className={cn(styles.overlay, open && styles.overlayOpen)}
        onClick={onClose}
        aria-hidden="true"
        data-testid="app-shell-overlay"
      />
      <div
        id="app-shell-drawer"
        ref={drawerRef}
        className={cn(styles.drawer, open && styles.drawerOpen)}
        {...(open ? { role: "dialog", "aria-modal": "true", "aria-label": t("dashboard_shell_main_menu") } : {})}
      >
        <div className={styles.drawerTop}>
          <Link to="/" onClick={onClose} className={styles.logoLink} aria-label={t("dashboard_shell_logo_label")}>
            <MainLogo className={styles.logoImage} />
          </Link>
          <button
            ref={closeRef}
            type="button"
            className={styles.drawerClose}
            onClick={onClose}
            aria-label={t("dashboard_shell_close_menu")}
          >
            <ShellIcon name="close" size={16} />
          </button>
        </div>
        <NavList nav={nav} activeId={activeId} onNavigate={onClose} inboxCount={inboxCount} />
      </div>
    </>
  );
};
